import { currency, formatDate, slug, sortNewest } from "../lib/format.js";
import { getLocalCalendarDate } from "../lib/date.js";
import { ActivityTable } from "../components/crmTables.jsx";
import { CustomerLink, EmployeeLink } from "../components/recordLinks.jsx";
import { Metric, MetricGrid, Panel, ResourceTable, StageChart, Topbar } from "../components/ui.jsx";
import { TeamTreeTable } from "../components/teamTreeTable.jsx";

export function Dashboard({
  user,
  businessDate,
  report,
  customers,
  activities,
  users,
  activityCustomFields = [],
  openCustomer,
  openActivity,
  openCall,
  openVisit,
  openMessage,
  openEmployee,
  openTarget,
  openExpense,
}) {
  const today = businessDate || getLocalCalendarDate();
  const pendingFollowups = customers.filter(
    (customer) => customer.followUpDate && customer.followUpDate <= today && !["Converted", "Won"].includes(customer.status),
  );
  const recentActivities = [...activities].sort(sortNewest).slice(0, 6);
  const actionQueue = buildDashboardActionQueue({ pendingFollowups, alerts: report?.alerts ?? [], targetPerformance: report?.targetPerformance ?? [], customers, today });

  return (
    <>
      <Topbar
        title="Dashboard"
        subtitle={user.role === "manager" ? "Team visibility across leads, calls, visits, and follow-ups." : "Your daily sales execution snapshot."}
      >
        <button className="primary-btn" onClick={() => openCustomer(null)}>
          Add Prospect
        </button>
        <button className="secondary-btn" onClick={openActivity}>
          Log Activity
        </button>
      </Topbar>
      <MetricGrid report={report} />
      <section className="metric-grid">
        <Metric label="Team Revenue Target" value={currency(report?.targetTotals?.revenueTarget ?? 0)} />
        <Metric label="Achieved Revenue" value={currency(report?.targetTotals?.achievedRevenue ?? 0)} />
        <Metric label="Target Achievement" value={`${report?.targetTotals?.achievementPercent ?? 0}%`} />
        <Metric label="Pending Gap" value={currency(report?.targetTotals?.pendingRevenueGap ?? 0)} />
      </section>
      <section className="panel spaced">
        <div className="panel-header">
          <div>
            <h2>Today's Action Queue</h2>
            <p className="subtle">The fastest path to the next customer or team action.</p>
          </div>
        </div>
        <DashboardActionQueue
          tasks={actionQueue}
          customers={customers}
          users={users}
          openCustomer={openCustomer}
          openEmployee={openEmployee}
          openCall={openCall}
          openVisit={openVisit}
          openMessage={openMessage}
          openTarget={openTarget}
          openExpense={openExpense}
        />
      </section>
      <section className="panel spaced">
        <h2>Team Reporting Roll-Up</h2>
        <TeamTreeTable rows={report?.teamRollups ?? []} onOpenEmployee={openEmployee} onAssignTarget={openTarget} onAddExpense={openExpense} />
      </section>
      <section className="content-grid">
        <Panel title="Lead Funnel" subtitle="Current customers by stage.">
          <StageChart stageCounts={report?.stageCounts ?? {}} />
        </Panel>
        <Panel title="Needs Attention" subtitle="Follow-ups due today or earlier.">
          <div className="list">
            {pendingFollowups.length ? (
              pendingFollowups.map((customer) => (
                <div className="list-item attention-row" key={customer.id}>
                  <div className="attention-row-body">
                    <strong>
                      <CustomerLink customer={customer} customers={customers} openCustomer={openCustomer} />
                    </strong>
                    <span>
                      {customer.phone} · Due {formatDate(customer.followUpDate)} · {customer.status}
                    </span>
                  </div>
                  <div className="attention-actions" aria-label={`${customer.name} quick actions`}>
                    <button className="mini-btn" type="button" onClick={() => openCall?.(customer)}>
                      Call
                    </button>
                    <button className="mini-btn" type="button" onClick={() => openVisit?.(customer)}>
                      Visit
                    </button>
                    {openMessage ? (
                      <button className="mini-btn" type="button" onClick={() => openMessage(customer, dashboardFollowUpMessageDraft(customer))}>
                        Message
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty">No pending follow-ups right now.</div>
            )}
          </div>
        </Panel>
      </section>
      <section className="panel spaced">
        <h2>Team Target Performance</h2>
        <TargetPerformanceTable
          rows={report?.targetPerformance ?? []}
          onOpenEmployee={openEmployee}
          onAssignTarget={openTarget}
          onAddExpense={openExpense}
        />
      </section>
      <section className="content-grid spaced">
        <Panel title="Recent Activity">
          <ActivityTable
            activities={recentActivities}
            customers={customers}
            users={users}
            activityCustomFields={activityCustomFields}
            openCustomer={openCustomer}
            openEmployee={openEmployee}
            openCall={openCall}
            openVisit={openVisit}
            openMessage={openMessage}
          />
        </Panel>
        <Panel title="Rep Activity" subtitle="Sales effort by team member.">
          <div className="list">
            {(report?.repSummary ?? []).map((rep) => (
              openEmployee ? (
                <button className="list-item list-item-button" type="button" key={rep.userId} onClick={() => openEmployee(rep.userId)}>
                  <strong>{rep.active === false ? `${rep.name} (inactive)` : rep.name}</strong>
                  <span>{rep.leads} leads · {rep.calls} calls · {rep.visits} visits · {rep.conversions} conversions</span>
                  <span className="list-item-action">Edit employee</span>
                </button>
              ) : (
                <div className="list-item" key={rep.userId}>
                  <strong>{rep.active === false ? `${rep.name} (inactive)` : rep.name}</strong>
                  <span>{rep.leads} leads · {rep.calls} calls · {rep.visits} visits · {rep.conversions} conversions</span>
                </div>
              )
            ))}
          </div>
        </Panel>
      </section>
      <section className="panel spaced">
        <h2>Alerts</h2>
        <DashboardAlerts
          alerts={report?.alerts ?? []}
          customers={customers}
          users={users}
          openCustomer={openCustomer}
          openEmployee={openEmployee}
          openCall={openCall}
          openVisit={openVisit}
          openMessage={openMessage}
          openTarget={openTarget}
          openExpense={openExpense}
        />
      </section>
    </>
  );
}

function DashboardActionQueue({
  tasks,
  customers,
  users,
  openCustomer,
  openEmployee,
  openCall,
  openVisit,
  openMessage,
  openTarget,
  openExpense,
}) {
  if (!tasks.length) return <div className="empty">No urgent actions right now.</div>;
  return (
    <ResourceTable
      rows={tasks}
      columns={[
        ["priority", "Priority"],
        ["title", "Work Item", (_value, task) => (
          task.customerId ? (
            <CustomerLink customerId={task.customerId} customers={customers} fallbackName={task.title} openCustomer={openCustomer} />
          ) : (
            <EmployeeLink userId={task.userId} users={users} openEmployee={openEmployee} />
          )
        )],
        ["reason", "Why Now"],
        ["ownerId", "Owner", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />],
      ]}
      action={(task) => (
        <ActionQueueButtons
          task={task}
          customers={customers}
          users={users}
          openCustomer={openCustomer}
          openCall={openCall}
          openVisit={openVisit}
          openMessage={openMessage}
          openTarget={openTarget}
          openExpense={openExpense}
        />
      )}
    />
  );
}

function ActionQueueButtons({ task, customers, users, openCustomer, openCall, openVisit, openMessage, openTarget, openExpense }) {
  const customer = customers.find((item) => item.id === task.customerId);
  const owner = customer ? users.find((item) => item.id === customer.ownerId) : null;
  const hasActiveOwner = !customer || owner?.active !== false;
  return (
    <div className="row-actions">
      {customer && !hasActiveOwner && openCustomer ? (
        <button className="mini-btn" type="button" onClick={() => openCustomer(customer)}>
          Reassign
        </button>
      ) : null}
      {customer && hasActiveOwner && openCall ? (
        <button className="mini-btn" type="button" onClick={() => openCall(customer)}>
          Call
        </button>
      ) : null}
      {customer && hasActiveOwner && openVisit ? (
        <button className="mini-btn" type="button" onClick={() => openVisit(customer)}>
          Visit
        </button>
      ) : null}
      {customer && hasActiveOwner && openMessage ? (
        <button className="mini-btn" type="button" onClick={() => openMessage(customer, task.messageDraft || dashboardFollowUpMessageDraft(customer))}>
          Message
        </button>
      ) : null}
      {!customer && task.userId && openTarget ? (
        <button className="mini-btn" type="button" onClick={() => openTarget(task, targetDraftFromRep(task))}>
          Assign Target
        </button>
      ) : null}
      {!customer && task.userId && openExpense ? (
        <button className="mini-btn" type="button" onClick={() => openExpense(task, expenseDraftFromRep(task))}>
          Add Expense
        </button>
      ) : null}
    </div>
  );
}

function DashboardAlerts({
  alerts,
  customers,
  users,
  openCustomer,
  openEmployee,
  openCall,
  openVisit,
  openMessage,
  openTarget,
  openExpense,
}) {
  return (
    <ResourceTable
      rows={alerts}
      columns={[
        ["severity", "Severity"],
        ["type", "Type"],
        ["customerId", "Customer", (value, row) => (
          value ? <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} /> : "-"
        )],
        ["message", "Message"],
        ["userId", "Owner", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />],
      ]}
      action={(row) => (
        <AlertActions
          alert={row}
          customers={customers}
          openCall={openCall}
          openVisit={openVisit}
          openMessage={openMessage}
          openTarget={openTarget}
          openExpense={openExpense}
        />
      )}
    />
  );
}

function AlertActions({ alert, customers, openCall, openVisit, openMessage, openTarget, openExpense }) {
  const customer = customers.find((item) => item.id === alert.customerId);
  const hasCustomerActions = Boolean(customer);
  const hasRepActions = Boolean(!customer && alert.userId);
  if (!hasCustomerActions && !hasRepActions) return <span className="subtle">-</span>;

  return (
    <div className="row-actions">
      {hasCustomerActions && openCall ? (
        <button className="mini-btn" type="button" onClick={() => openCall(customer)}>
          Call
        </button>
      ) : null}
      {hasCustomerActions && openVisit ? (
        <button className="mini-btn" type="button" onClick={() => openVisit(customer)}>
          Visit
        </button>
      ) : null}
      {hasCustomerActions && openMessage ? (
        <button className="mini-btn" type="button" onClick={() => openMessage(customer, alertMessageDraft(alert, customer))}>
          Message
        </button>
      ) : null}
      {hasRepActions && openTarget ? (
        <button className="mini-btn" type="button" onClick={() => openTarget(alert, alertTargetDraft(alert))}>
          Assign Target
        </button>
      ) : null}
      {hasRepActions && openExpense ? (
        <button className="mini-btn" type="button" onClick={() => openExpense(alert, alertExpenseDraft(alert))}>
          Add Expense
        </button>
      ) : null}
    </div>
  );
}

function TargetPerformanceTable({ rows, onOpenEmployee, onAssignTarget, onAddExpense }) {
  if (!rows.length) return <div className="empty">No target data available yet.</div>;
  const hasActions = Boolean(onAssignTarget || onAddExpense);
  return (
    <div className="table-wrap">
      <table className="performance-table">
        <thead>
          <tr>
            <th>Team Member</th>
            <th>Revenue Target</th>
            <th>Achieved</th>
            <th>Progress</th>
            <th>Gap</th>
            <th>Leads / Visits / Orders</th>
            <th>Status</th>
            {hasActions ? <th>Action</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((rep) => (
            <tr key={rep.userId}>
              <td>
                {onOpenEmployee ? (
                  <button className="table-link-btn" type="button" onClick={() => onOpenEmployee(rep.userId)}>
                    {rep.active === false ? `${rep.name} (inactive)` : rep.name}
                  </button>
                ) : <strong>{rep.active === false ? `${rep.name} (inactive)` : rep.name}</strong>}
                <br />
                <span className="subtle">
                  {rep.territory} · {rep.productCategory} · {rep.period}
                </span>
              </td>
              <td>{currency(rep.revenueTarget)}</td>
              <td>{currency(rep.achievedRevenue)}</td>
              <td>
                <div className="progress-cell">
                  <strong>{rep.revenueAchievementPercent}%</strong>
                  <div className="progress-track" aria-label={`${rep.name} target progress ${rep.revenueAchievementPercent}%`}>
                    <div className="progress-fill" style={{ width: `${Math.min(100, rep.revenueAchievementPercent)}%` }} />
                  </div>
                </div>
              </td>
              <td>{currency(rep.pendingRevenueGap)}</td>
              <td>
                {rep.leadCount} / {rep.visitCount} / {rep.orderCount}
              </td>
              <td>
                <span className={`performance-status ${slug(rep.status)}`}>{rep.status}</span>
              </td>
              {hasActions ? (
                <td>
                  <div className="row-actions">
                    {onAssignTarget ? (
                      <button
                        className="mini-btn"
                        type="button"
                        disabled={rep.active === false}
                        onClick={() => onAssignTarget(rep, targetDraftFromRep(rep))}
                      >
                        Assign Target
                      </button>
                    ) : null}
                    {onAddExpense ? (
                      <button
                        className="mini-btn"
                        type="button"
                        disabled={rep.active === false}
                        onClick={() => onAddExpense(rep, expenseDraftFromRep(rep))}
                      >
                        Add Expense
                      </button>
                    ) : null}
                  </div>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function targetDraftFromRep(rep) {
  return {
    productCategory: rep.productCategory || "All",
    period: rep.period || "Monthly",
    revenueTarget: Number(rep.revenueTarget || 0),
    quantityTarget: Number(rep.quantityTarget || 0),
  };
}

function expenseDraftFromRep(rep) {
  return {
    purpose: `${rep.productCategory || "Sales"} target travel`,
  };
}

function dashboardFollowUpMessageDraft(customer) {
  const dueLabel = customer.followUpDate ? ` due on ${formatDate(customer.followUpDate)}` : "";
  return {
    channel: "WhatsApp",
    template: "Follow-up",
    message: `Following up on ${customer.name}${dueLabel}.`,
    consentRecorded: customer.consentOptIn !== false,
  };
}

function alertMessageDraft(alert, customer) {
  return {
    channel: "WhatsApp",
    template: alert.type === "Credit block" ? "Order update" : "Follow-up",
    message: `${alert.message}. Please confirm the next action for ${customer.name}.`,
    consentRecorded: customer.consentOptIn !== false,
  };
}

function alertTargetDraft(alert) {
  return {
    productCategory: alert.productCategory || "All",
    period: alert.period || "Monthly",
    revenueTarget: Number(alert.revenueTarget || 0),
    quantityTarget: Number(alert.quantityTarget || 0),
  };
}

function alertExpenseDraft(alert) {
  return {
    purpose: `${alert.productCategory || alert.type || "Alert"} follow-up`,
  };
}

function buildDashboardActionQueue({ pendingFollowups, alerts, targetPerformance, customers, today }) {
  const followUpTasks = [];
  const alertTasks = [];
  const targetTasks = [];
  const seen = new Set();
  const customersById = new Map(customers.map((customer) => [customer.id, customer]));
  const addCustomerTask = (task) => {
    if (!task.customerId || seen.has(`customer:${task.customerId}:${task.type}`)) return;
    seen.add(`customer:${task.customerId}:${task.type}`);
    return task;
  };

  pendingFollowups
    .sort((a, b) => String(a.followUpDate || "").localeCompare(String(b.followUpDate || "")))
    .slice(0, 3)
    .forEach((customer) => {
      const task = addCustomerTask({
        id: `queue-follow-up-${customer.id}`,
        type: "follow-up",
        priority: customer.followUpDate < today ? "High" : "Today",
        title: customer.name,
        reason: `Follow-up due ${formatDate(customer.followUpDate)} · ${customer.status}`,
        customerId: customer.id,
        ownerId: customer.ownerId,
        messageDraft: dashboardFollowUpMessageDraft(customer),
      });
      if (task) followUpTasks.push(task);
    });

  alerts
    .filter((alert) => alert.customerId && customersById.has(alert.customerId))
    .slice(0, 3)
    .forEach((alert) => {
      const customer = customersById.get(alert.customerId);
      const task = addCustomerTask({
        id: `queue-alert-${alert.id}`,
        type: "alert",
        priority: alert.severity || "Review",
        title: customer?.name || alert.message,
        reason: alert.message,
        customerId: alert.customerId,
        ownerId: alert.userId,
        messageDraft: alertMessageDraft(alert, customer),
      });
      if (task) alertTasks.push(task);
    });

  targetPerformance
    .filter((rep) => rep.active !== false && Number(rep.revenueAchievementPercent || 0) < 60)
    .sort((a, b) => Number(a.revenueAchievementPercent || 0) - Number(b.revenueAchievementPercent || 0))
    .slice(0, 4)
    .forEach((rep) => targetTasks.push({
      ...rep,
      id: `queue-target-${rep.userId}-${rep.productCategory || "all"}`,
      type: "target",
      priority: Number(rep.revenueAchievementPercent || 0) < 40 ? "High" : "Medium",
      title: rep.name,
      reason: `${rep.productCategory || "Sales"} target at ${rep.revenueAchievementPercent || 0}% · gap ${currency(rep.pendingRevenueGap || 0)}`,
      ownerId: rep.userId,
    }));

  return [...followUpTasks, ...alertTasks, ...targetTasks].slice(0, 8);
}
